import {
    ArrowsMergeIcon,
    EyeSlashIcon,
    NotePencilIcon,
    PencilIcon,
    TrashIcon,
    type Icon,
} from '@phosphor-icons/react';
import type { TFunction } from 'i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { EntityKind } from '@/types/identity';

export type IdentityActionKind =
    | 'edit'
    | 'markAsUnknown'
    | 'markAsRecognized'
    | 'merge'
    | 'delete';

export interface IdentityActionDescriptor {
    kind: IdentityActionKind;
    icon: Icon;
    label: string;
}

const ACTION_ICONS: Record<IdentityActionKind, Icon> = {
    edit: PencilIcon,
    markAsUnknown: EyeSlashIcon,
    markAsRecognized: NotePencilIcon,
    merge: ArrowsMergeIcon,
    delete: TrashIcon,
};

const ACTION_LABEL_KEYS: Record<
    EntityKind,
    Record<IdentityActionKind, string>
> = {
    person: {
        edit: 'personPage.edit',
        markAsUnknown: 'persons.card.markAsUnknown',
        markAsRecognized: 'persons.card.markAsRecognized',
        merge: 'mergeIdentityModal.mergeInto',
        delete: 'persons.card.delete',
    },
    vehicle: {
        edit: 'vehiclePage.edit',
        markAsUnknown: 'vehicles.card.markAsUnknown',
        markAsRecognized: 'vehicles.card.markAsRecognized',
        merge: 'mergeVehicleModal.mergeInto',
        delete: 'vehicles.card.delete',
    },
};

const ACTION_ORDER: Record<
    EntityKind,
    Record<'known' | 'unknown', IdentityActionKind[]>
> = {
    person: {
        known: ['edit', 'markAsUnknown', 'merge'],
        unknown: ['markAsRecognized', 'merge', 'delete'],
    },
    vehicle: {
        known: ['edit', 'markAsUnknown', 'merge'],
        unknown: ['markAsRecognized', 'delete', 'merge'],
    },
};

interface IdentityActionDescriptorOptions {
    entityKind: EntityKind;
    known: boolean;
    includeDelete: boolean;
    t: TFunction;
}

export function getIdentityActionDescriptors({
    entityKind,
    known,
    includeDelete,
    t,
}: IdentityActionDescriptorOptions): IdentityActionDescriptor[] {
    return ACTION_ORDER[entityKind][known ? 'known' : 'unknown']
        .filter((kind) => includeDelete || kind !== 'delete')
        .map((kind) => ({
            kind,
            icon: ACTION_ICONS[kind],
            label: t(ACTION_LABEL_KEYS[entityKind][kind], {
                ns: I18nNamespace.DIRECTORY,
            }),
        }));
}
